import { Outlet } from '@tanstack/react-router';
import { motion } from 'motion/react';
import { useEffect, useMemo, useState } from 'react';
import { usePathname } from '@/hooks/use-pathname';
import type { ac } from '@/lib/permissions';
import { ForbiddenPage } from '@/pages/+not-allowed';
import { PageHeaderProvider } from '@/providers/page-header/page-header.provider';
import { usePermissions } from '@/services/auth';
import { AppSidebar } from '../ui/app-sidebar';
import { AppTopbar } from '../ui/app-topbar';
import { SidebarInset, SidebarProvider } from '../ui/sidebar';

export function AppLayout() {
  const [hasEntered, setHasEntered] = useState(false);

  const pathname = usePathname();

  const { hasPermission } = usePermissions();

  const { resource, action } = useMemo(() => {
    if (pathname === '/') {
      return {
        resource: 'dashboard' as keyof typeof ac.statements,
        action: 'browse',
      };
    }

    const segments = pathname.split('/').filter(Boolean);

    const resource = segments[0] as keyof typeof ac.statements;

    let action: 'browse' | 'read' | 'create' | 'update' = 'browse';

    if (segments[1] === 'create') {
      action = 'create';
    } else if (segments[2] === 'view') {
      action = 'read';
    } else if (segments[1]) {
      action = 'update';
    }

    return {
      resource,
      action,
    };
  }, [pathname]);

  useEffect(() => {
    setHasEntered(true);
  }, []);

  if (!hasPermission(resource, action as any)) {
    return <ForbiddenPage />;
  }

  return (
    <PageHeaderProvider>
      <SidebarProvider>
        <motion.div
          className="print:hidden"
          initial={hasEntered ? false : { x: -40, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
        >
          <AppSidebar />
        </motion.div>

        <SidebarInset>
          {/* Coluna `minmax(0,1fr)`, não `auto`: senão o mínimo do topo (botões + trilha + ação) alarga a página no celular. */}
          <div className="grid min-w-0 grid-cols-[minmax(0,1fr)] transition-all duration-300 print:block">
            <div className="print:hidden">
              <AppTopbar />
            </div>

            <div className="min-w-0 px-2 md:px-6 py-8 overflow-auto h-full print:p-0 print:overflow-visible">
              <Outlet />
            </div>
          </div>
        </SidebarInset>
      </SidebarProvider>
    </PageHeaderProvider>
  );
}
